(() => {
  'use strict';

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  const lottieSource = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
  let lottiePromise;

  const loadLottie = () => {
    if (window.lottie?.loadAnimation) return Promise.resolve(window.lottie);
    if (lottiePromise) return lottiePromise;

    lottiePromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-izzat-lottie]');
      const script = existing || document.createElement('script');
      const onLoad = () => window.lottie?.loadAnimation
        ? resolve(window.lottie)
        : reject(new Error('Lottie loaded without its public API.'));
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', () => reject(new Error('Unable to load Lottie.')), { once: true });
      if (!existing) {
        script.src = lottieSource;
        script.async = true;
        script.dataset.izzatLottie = '';
        document.head.append(script);
      }
    }).catch((error) => {
      lottiePromise = undefined;
      throw error;
    });

    return lottiePromise;
  };

  class IzzatPassiveElement extends HTMLElement {}

  class IzzatJsonAnimation extends HTMLElement {
    connectedCallback() {
      if (this._loading || this._initialized) return;
      this._loading = true;
      loadLottie()
        .then(() => {
          if (!this.isConnected || this._initialized) return;
          this._initialized = true;
          this._loading = false;
          this.desktopUrl = this.getAttribute('animation-url') || '';
          this.mobileUrl = this.getAttribute('animation-url-mobile') || '';
          if (!this.desktopUrl) return;
          this.loop = this.booleanAttribute('loop', true);
          this.autoplay = this.booleanAttribute('autoplay', true);
          this.speed = Number.parseFloat(this.getAttribute('speed')) || 1;
          this._onResize = () => {
            window.clearTimeout(this._resizeTimer);
            this._resizeTimer = window.setTimeout(() => this.renderAnimation(), 250);
          };
          window.addEventListener('resize', this._onResize);
          this.renderAnimation();
        })
        .catch((error) => {
          this._loading = false;
          console.warn(error.message);
        });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.clearTimeout(this._resizeTimer);
      this._observer?.disconnect();
      this._animation?.destroy();
      this._observer = undefined;
      this._animation = undefined;
      this._initialized = false;
      this._loading = false;
    }

    booleanAttribute(name, fallback) {
      if (!this.hasAttribute(name)) return fallback;
      return ['', 'true', '1'].includes(this.getAttribute(name));
    }

    currentUrl() {
      return window.innerWidth < 750 && this.mobileUrl ? this.mobileUrl : this.desktopUrl;
    }

    renderAnimation() {
      const nextUrl = this.currentUrl();
      if (!nextUrl || nextUrl === this._currentUrl) return;
      this._currentUrl = nextUrl;
      this._observer?.disconnect();
      this._animation?.destroy();
      this.replaceChildren();
      this._animation = window.lottie.loadAnimation({
        container: this,
        renderer: 'svg',
        loop: this.loop,
        autoplay: this.autoplay,
        path: nextUrl,
      });
      this._animation.setSpeed(this.speed);
      if (!this.autoplay) {
        this._observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this._animation?.play();
            else if (this.loop) this._animation?.pause();
          }
        }, { threshold: 0.3 });
        this._observer.observe(this);
      }
    }
  }

  class IzzatComparisonSlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.input = this.querySelector('input[type="range"]');
      this.overlay = this.querySelector('.comparison-slider__overlay');
      this._onInput = () => {
        const value = Math.min(100, Math.max(0, Number(this.input?.value) || 50));
        if (this.overlay) this.overlay.style.width = `${value}%`;
        this.style.setProperty('--comparison-position', `${value}%`);
      };
      this.input?.addEventListener('input', this._onInput);
      this._onInput();
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this._onInput);
      this._initialized = false;
    }
  }

  class IzzatInternalVideo extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.video = this.querySelector('video');
      this.playButton = this.querySelector('.internal-video__play');
      this.soundButton = this.querySelector('.internal-video__sound-btn');
      this._onPlay = () => this.togglePlayback();
      this._onSound = () => this.toggleSound();
      this._onVideoPlay = () => this.classList.add('internal-video--playing');
      this._onVideoPause = () => this.classList.remove('internal-video--playing');
      this.playButton?.addEventListener('click', this._onPlay);
      this.soundButton?.addEventListener('click', this._onSound);
      this.video?.addEventListener('play', this._onVideoPlay);
      this.video?.addEventListener('pause', this._onVideoPause);
      if (this.dataset.autoplay === 'true') this.deferAutoplay();
    }

    disconnectedCallback() {
      this.playButton?.removeEventListener('click', this._onPlay);
      this.soundButton?.removeEventListener('click', this._onSound);
      this.video?.removeEventListener('play', this._onVideoPlay);
      this.video?.removeEventListener('pause', this._onVideoPause);
      this._visibilityObserver?.disconnect();
      this._visibilityObserver = undefined;
      this._initialized = false;
    }

    hydrateSources() {
      if (!this.video || this.video.dataset.sourcesLoaded === 'true') return;
      let hydrated = false;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        hydrated = true;
      });
      if (this.video.dataset.src) {
        this.video.src = this.video.dataset.src;
        this.video.removeAttribute('data-src');
        hydrated = true;
      }
      this.video.dataset.sourcesLoaded = 'true';
      if (hydrated) this.video.load();
    }

    deferAutoplay() {
      if (!this.video) return;
      const playWhenVisible = () => {
        this.hydrateSources();
        this.video.muted = true;
        this.classList.add('internal-video--muted');
        this.video.play().catch(() => {});
      };

      if (!('IntersectionObserver' in window)) {
        playWhenVisible();
        return;
      }

      this._visibilityObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible) playWhenVisible();
        else if (!this.video.paused) this.video.pause();
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      this._visibilityObserver.observe(this);
    }

    togglePlayback() {
      if (!this.video) return;
      if (this.video.paused) {
        this.hydrateSources();
        this.video.play().catch(() => {});
      }
      else this.video.pause();
    }

    toggleSound() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.classList.toggle('internal-video--muted', this.video.muted);
    }
  }

  class IzzatCustomProductField extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.control = this.querySelector('input:not([type="radio"]), textarea, select, input[type="radio"]:checked');
      this._onChange = () => this.sync();
      this.addEventListener('change', this._onChange);
      this.addEventListener('input', this._onChange);
      this._onSubmit = (event) => this.validate(event);
      this._onVariantChange = (event) => {
        if (String(event.detail?.sectionId || '') === String(this.dataset.section || '')) this.bindForm();
      };
      document.addEventListener('izzat-theme-v2:variant-change', this._onVariantChange);
      this.bindForm();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('input', this._onChange);
      document.removeEventListener('izzat-theme-v2:variant-change', this._onVariantChange);
      this.form?.removeEventListener('submit', this._onSubmit, true);
      this.hidden?.remove();
      this.form = undefined;
      this.hidden = undefined;
      this._initialized = false;
    }

    bindForm() {
      const section = String(this.dataset.section || '');
      const formId = `product-form-${section}`;
      const nextForm = document.getElementById(formId)?.querySelector('form')
        || document.querySelector(`product-form[data-section="${CSS.escape(section)}"] form`);
      if (nextForm === this.form && this.hidden) {
        this.sync();
        return true;
      }
      this.form?.removeEventListener('submit', this._onSubmit, true);
      this.hidden?.remove();
      this.form = nextForm;
      this.hidden = undefined;
      if (!this.form) return false;
      this.hidden = document.createElement('input');
      this.hidden.type = 'hidden';
      this.hidden.name = `properties[${this.dataset.name || ''}]`;
      this.form.appendChild(this.hidden);
      this.form.addEventListener('submit', this._onSubmit, true);
      this.sync();
      return true;
    }

    value() {
      return this.querySelector('input[type="radio"]:checked')?.value || this.querySelector('input:not([type="radio"]), textarea, select')?.value || '';
    }

    sync() {
      if (this.hidden) this.hidden.value = this.value();
      this.classList.remove('custom-product-field--error');
    }

    validate(event) {
      if (this.dataset.required === 'true' && !this.value().trim()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.classList.add('custom-product-field--error');
        this.querySelector('.input-error-msg')?.removeAttribute('hidden');
      }
    }
  }

  class IzzatSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._onClick = (event) => {
        const box = event.target.closest('.subscription__box');
        if (!box || !this.contains(box)) return;
        this.select(box);
      };
      this.addEventListener('click', this._onClick);
      this.select(this.querySelector('.subscription__box--selected') || this.querySelector('.subscription__box'));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._initialized = false;
    }

    select(box) {
      if (!box) return;
      this.querySelectorAll('.subscription__box').forEach((item) => {
        item.classList.toggle('subscription__box--selected', item === box);
        item.dataset.selected = item === box ? 'true' : 'false';
      });
      const section = this.dataset.section || this.id.replace('SubscriptionWidget-', '');
      const form = document.querySelector(`product-form[data-section="${CSS.escape(section)}"] form`);
      if (!form) return;
      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      const selectedPlan = box.querySelector('[data-selling-plan-id].selected, [data-selling-plan-id]:checked, [data-selling-plan-id]');
      input.value = box.dataset.type === 'subscription' ? selectedPlan?.dataset.sellingPlanId || this.dataset.firstSellingPlanId || '' : '';
      input.disabled = !input.value;
    }
  }

  class IzzatPromoPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.storageKey = `izzat-promo-popup-${this.id || 'default'}`;
      this._onClick = (event) => {
        if (event.target.closest('.promp-popup__close-btn, .popup-modal__close, .popup-modal__dismiss-btn')) this.close();
      };
      this.addEventListener('click', this._onClick);
      if (this.dataset.testMode === 'true' || localStorage.getItem(this.storageKey) !== 'closed') {
        window.setTimeout(() => this.open(), Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._initialized = false;
    }

    open() {
      this.querySelector('.sign-up-popup-overlay')?.classList.add('popup-overlay--active');
      this.querySelector('.sign-up-popup-modal')?.classList.add('popup-modal--active');
    }

    close() {
      this.querySelectorAll('.popup-overlay--active, .popup-modal--active').forEach((item) => item.classList.remove('popup-overlay--active', 'popup-modal--active'));
      if (this.dataset.testMode !== 'true') localStorage.setItem(this.storageKey, 'closed');
    }
  }

  class IzzatCardVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._onChange = (event) => {
        const option = event.target.selectedOptions?.[0] || event.target;
        const variantId = option.dataset.variantId || option.value;
        const card = this.closest('.card-wrapper, .card');
        const formInput = card?.querySelector('input[name="id"]');
        if (formInput && /^\d+$/.test(String(variantId))) formInput.value = variantId;
        const url = option.dataset.url;
        if (url) card?.querySelectorAll('a[href]').forEach((link) => { link.href = url; });
      };
      this.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this._initialized = false;
    }
  }

  class IzzatQuantityBreakUpsells extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._onClick = (event) => {
        const item = event.target.closest('.quantity-break__upsell[data-toggle="true"]');
        if (!item || !this.contains(item)) return;
        item.dataset.selected = item.dataset.selected === 'true' ? 'false' : 'true';
        item.classList.toggle('selected', item.dataset.selected === 'true');
      };
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._initialized = false;
    }
  }

  define('card-variant-selects', IzzatCardVariantSelects);
  define('comparison-slider', IzzatComparisonSlider);
  define('custom-product-field', IzzatCustomProductField);
  define('internal-video', IzzatInternalVideo);
  define('json-animation', IzzatJsonAnimation);
  define('pickup-availability-preview', class extends IzzatPassiveElement {});
  define('product-info', class extends IzzatPassiveElement {});
  define('promo-popup', IzzatPromoPopup);
  define('quantity-break-upsells', IzzatQuantityBreakUpsells);
  define('quantity-gifts', class extends IzzatPassiveElement {});
  define('search-form', class extends IzzatPassiveElement {});
  define('section-group', class extends IzzatPassiveElement {});
  define('subscription-widget', IzzatSubscriptionWidget);
})();
